import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MsalService } from '@azure/msal-angular';
import { QimaOptionalType } from '@qima/ngx-qima';
import { NotificationService } from '../../../services/notification.service';
import { Credentials, Session, Token, User } from '../../models';
import { GlobalEnvironmentService } from '../global-environment.service';
import { LogrocketInitService } from '../logrocket-init/logrocket-init.service';
import { StateService } from '../state/state.service';

interface AuthResponse {
  content: {
    employee: {
      userId: string;
      firstName: string;
      lastName: string;
      email: string;
      roles: Record<string, string[]> | string[];
    };
    token: {
      id: string;
      userId: string;
      userType: string;
      token: string;
      validBefore: string;
      refreshKey: string;
    };
  };
}

interface UserResponse {
  content: {
    employee: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      roles: Record<string, string[]> | string[];
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly stateService: StateService,
    private readonly notificationService: NotificationService,
    private readonly globalEnvironmentService: GlobalEnvironmentService,
    private readonly msalService: MsalService,
    private readonly logrocketInitService: LogrocketInitService
  ) {}

  public signIn(credentials: Credentials): Observable<Session> {
    this.stateService.setLoading(true);

    return this.http.post<AuthResponse>(`${this.globalEnvironmentService.getPublicApiUrl()}/auth/v2/token`, credentials).pipe(
      map(response => {
        const session = this.buildSessionFromAuthResponse(response);

        this.stateService.setSession(session);
        this.stateService.setLoading(false);
        this.logrocketInitService.syncAuthenticatedSession();

        this.notificationService.showSuccess('AUTH.LOGIN_SUCCESS');

        return session;
      }),
      catchError((error: unknown) => {
        this.stateService.setLoading(false);
        this.handleAuthError(error);

        return throwError(() => error);
      })
    );
  }

  public signOut(redirectTo?: QimaOptionalType<string[]>): void {
    const loginWithMsal = this.stateService.getStateValue<boolean | undefined>('loginWithMsal');

    this.clearSession();

    if (loginWithMsal === true) {
      const accounts = this.msalService.instance.getAllAccounts();

      if (accounts.length > 0) {
        this.msalService.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: new URL('/login', globalThis.location.origin).href,
        });
        return;
      }
    }

    if (redirectTo) {
      void this.router.navigate(redirectTo);
    }

    this.notificationService.showInfo('AUTH.LOGOUT_SUCCESS');
  }

  public signWithAuthenticationProfile(credentials: Credentials, accessToken: string): Observable<Session> {
    this.stateService.setLoading(true);

    const url = `${this.globalEnvironmentService.getPublicApiUrl()}/auth/v2/qima-office`;
    const headers = new HttpHeaders({
      'Qima-Connect-Access-Token': accessToken,
    });

    return this.http.post<AuthResponse>(url, credentials, { headers }).pipe(
      map(response => {
        const session = this.buildSessionFromAuthResponse(response);

        this.stateService.setSession({
          user: session.user,
          token: session.token,
          isAuthenticated: true,
          loginWithMsal: true,
        });
        this.stateService.setLoading(false);
        this.logrocketInitService.syncAuthenticatedSession();
        this.notificationService.showSuccess('AUTH.LOGIN_SUCCESS');

        return session;
      }),
      catchError((error: unknown) => {
        this.stateService.setLoading(false);
        this.handleAuthError(error);

        return throwError(() => error);
      })
    );
  }

  public getCurrentUser(): Observable<User> {
    return this.http.get<UserResponse>(`${this.globalEnvironmentService.getFoodCertificationApiUrl()}/auth/v2/me`).pipe(
      map(
        response =>
          new User({
            id: response.content.employee.id,
            username: response.content.employee.email,
            firstName: response.content.employee.firstName,
            lastName: response.content.employee.lastName,
            email: response.content.employee.email,
            roles: response.content.employee.roles,
            isActive: true,
          })
      ),
      catchError((error: unknown) => {
        this.handleAuthError(error);

        return throwError(() => error);
      })
    );
  }

  private buildSessionFromAuthResponse(response: AuthResponse): Session {
    const { employee, token } = response.content;
    const user = new User({
      id: employee.userId,
      username: employee.email,
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      roles: employee.roles,
      isActive: true,
    });
    const tokenModel = new Token({
      token: token.token,
      validBefore: parseInt(token.validBefore, 10),
      userId: token.userId,
    });

    return new Session({
      user,
      token: tokenModel,
      isAuthenticated: true,
    });
  }

  private clearSession(): void {
    this.stateService.clearSession();
  }

  private handleAuthError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 401:
          this.notificationService.showError('AUTH.ERROR_UNAUTHORIZED');
          break;
        case 403:
          this.notificationService.showError('AUTH.ERROR_FORBIDDEN');
          break;
        case 404:
          this.notificationService.showError('AUTH.ERROR_NOT_FOUND');
          break;
        case 500:
          this.notificationService.showError('AUTH.ERROR_SERVER');
          break;
        default:
          this.notificationService.showError('AUTH.ERROR_UNKNOWN');
      }
    } else {
      this.notificationService.showError('AUTH.ERROR_UNKNOWN');
    }
  }
}
